import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import moment from 'moment';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';

import { mapProps } from '../../../../../../util/hocs';
import Button from '../../../../../../components/global/Button';
import YearResultListing from './components/YearResultListing';
import { getSearchResultsFromHierarchyItems } from './helpers';
import ShutterbugUserPageNullState from './ShutterbugUserPageNullState';
import { shutterbugAccountProfile } from '../../graphql';
import { ACCOUNT_PROFILE_HIERARCHY_ITEMS_SORT } from '../../helpers';
import { SORT_DIRECTION } from '../../../../../../constants/sort';
import { createLoadMoreMethod } from '../../../../../../util/apollo';
import { APOLLO_NETWORK_STATUS } from '../../../../../../constants/apollo-network-status';
import FullScreenLoader from '../../../../../../components/global/FullScreenLoader';
import ProfilePageHero from '../ProfilePageHero/ProfilePageHero';
import NotFound from '../../../../../pro/views/NotFound';

const propTypes = {
  loading: PropTypes.bool,
  accountProfile: PropTypes.object,
  loadMore: PropTypes.func,
  loadingMore: PropTypes.bool,
  onGoBack: PropTypes.func,
};

const NO_YEAR = 'NO_YEAR';

const ShutterbugUserPage = ({
  loading = true,
  accountProfile,
  loadMore,
  loadingMore,
  onGoBack,
}) => {
  if (loading) {
    return <FullScreenLoader />;
  }

  const { username, hierarchy_items: hierarchyItems } = accountProfile;

  // Flatten galleries and albums, while tidying data we want.
  const results = getSearchResultsFromHierarchyItems(hierarchyItems.records);

  const hierarchyItemsTotal = hierarchyItems?.total ?? 0;

  const hasEmptyContent = hierarchyItemsTotal === 0 && !loading;

  // Group all individual results by year.
  const yearGroupedResults = {};
  results.forEach(result => {
    const yearOfResult = result.startDate
      ? moment(result.startDate).format('YYYY')
      : NO_YEAR;

    // Add result to - and/or create a - year for specific year.
    yearGroupedResults[yearOfResult] = yearGroupedResults[yearOfResult]
      ? [...yearGroupedResults[yearOfResult], result]
      : [result];
  });

  if (true) {
    // Don't plan to delete this for the time being,
    // Maybe we come back to it (1/25/2022)
    return <NotFound />;
  }

  if (hasEmptyContent) {
    return (
      <ShutterbugUserPageNullState
        accountProfile={accountProfile}
        onGoBack={onGoBack}
      />
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>Albums by {username} &ndash; Waldo</title>
      </Helmet>
      <div className="generic-user-page">
        <ProfilePageHero accountProfile={accountProfile} onGoBack={onGoBack} />
        {/**
         * Object.keys will return alphabetized (eg 2016, 2017, 2018).
         * So reverse it to make it latest first.
         */}
        <div className="pt-2">
          {Object.keys(yearGroupedResults)
            .reverse()
            .map(
              year =>
                year !== NO_YEAR && (
                  <YearResultListing
                    year={year}
                    key={year}
                    hierarchyResults={yearGroupedResults[year]}
                  />
                ),
            )}

          {yearGroupedResults[NO_YEAR] && (
            <YearResultListing
              year="Galleries and albums without a date"
              hierarchyResults={yearGroupedResults[NO_YEAR]}
            />
          )}

          {hierarchyItems.records.length < hierarchyItemsTotal && (
            <Button
              btnStyle="yellow"
              className="mt-4 load-more-button"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'View More'}
            </Button>
          )}
        </div>
      </div>
    </Fragment>
  );
};

ShutterbugUserPage.propTypes = propTypes;

export { ShutterbugUserPage as ShutterbugUserPageComponent };

const withShutterbugAccountProfile = graphql(shutterbugAccountProfile, {
  options: ({ match }) => ({
    notifyOnNetworkStatusChange: true,
    variables: {
      username: match.params.username,
      sort: {
        field: ACCOUNT_PROFILE_HIERARCHY_ITEMS_SORT.START_DATE,
        direction: SORT_DIRECTION.DESC,
      },
      with_join_code_only: false,
      web_galleries_only: false,
    },
  }),
  skip: ({ match }) => !match.params.username,
});

export default compose(
  withRouter,
  withShutterbugAccountProfile,
  mapProps(({ data, ...props }) => ({
    ...props,
    accountProfile: data?.accountProfile,
    loading:
      data?.loading && data?.networkStatus === APOLLO_NETWORK_STATUS.LOADING,
    loadingMore: data?.networkStatus === APOLLO_NETWORK_STATUS.FETCH_MORE, // loadMore
    loadMore: createLoadMoreMethod({
      source: data?.accountProfile,
      recordsPath: 'hierarchy_items',
      fetchMore: data?.fetchMore,
    }),
  })),
)(ShutterbugUserPage);
